import { useState, useEffect, useRef } from "react";
import { DataListInput, DateInput, TextInput } from "../forms/Input";
import { Flag01Icon } from "hugeicons-react";
import { PrimaryButton, SecondaryButton } from "../forms/buttons";
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { API_URL } from '../../api';
import MessageDescription from "../PopoupMessage/messageDescription";

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.error('Invalid date string:', dateString);
    return null;
  }
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Axios interceptor (no alerts)
axios.interceptors.response.use(
  response => response,
  error => {
    console.error('Axios intercept error:', {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        data: error.response.data,
        url: error.response.config.url
      } : null,
      config: error.config
    });
    return Promise.reject(error);
  }
);

const EditTarget = ({ targetData, onClose, onSubmit, id }) => {
  const [formData, setFormData] = useState(targetData);
  const [changedFields, setChangedFields] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [measures, setMeasures] = useState([]);
  const [typeOptions] = useState(['numerical', 'percentage']);
  const [type, setType] = useState(targetData.type || 'numerical');
  const [valuesOptions] = useState(['Lower is better', 'Greater is better', 'N/A']);
  const [values, setValues] = useState(targetData.value_condition || '');

  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const fetchMeasures = async () => {
      const token = Cookies.get('accessToken');
      setIsLoading(true);
      try {
        console.log('Fetching measures for year 2025 with measure_unit=Numerical');
        const response = await axios.get(`${API_URL}/measures/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          params: {
            measure_year: 2025,
            measure_unit: 'Numerical'
          },
        });
        console.log('Measures API response:', {
          status: response.status,
          data: response.data
        });

        let measuresData = response.data;
        if (!Array.isArray(measuresData)) {
          measuresData = measuresData?.results || measuresData?.data || [];
          console.log('Normalized measures data:', measuresData);
        }

        if (!Array.isArray(measuresData)) {
          throw new Error('Response data is not an array');
        }

        const measuresArray = measuresData.map(item => ({
          id: item.id,
          name: item.name || item['name']
        }));
        console.log('Processed measures:', measuresArray);
        setMeasures(measuresArray);

        if (measuresArray.length === 0) {
          toast.error('No measures found for 2025.');
        }
      } catch (error) {
        console.error('Failed to fetch measures:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        toast.error('Failed to load measures');
        setMeasures([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeasures();
  }, []);

  useEffect(() => {
    setFormData(targetData);
    setValues(targetData.value_condition || '');
    setType(targetData.type || 'numerical');
  }, [targetData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field change - ${name}:`, {
    oldValue: formData[name],
    newValue: value
    });

    let processedValue = value;

    if (name === 'value') {
      if (value === '') {
        processedValue = '';
      } else {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          console.warn('Invalid numeric value:', value);
          toast.error('Target value must be a valid number');
          return;
        }
        processedValue = numValue;
      }
    }

    setChangedFields(prev => new Set(prev).add(name));
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleTypeChange = (value) => {
    console.log('Type change:', { oldValue: type, newValue: value });
    if (value && ['numerical', 'percentage'].includes(value)) {
      setType(value);
      setChangedFields(prev => new Set(prev).add('type'));
      setFormData(prev => ({
        ...prev,
        type: value
      }));
    } else {
      console.warn('Invalid type value:', value);
      toast.error('Please select a valid type (numerical or percentage)');
    }
  };

  const handleValuesChange = (value) => {
    console.log('Values change:', { oldValue: values, newValue: value });
    if (value) {
      setValues(value);
      setChangedFields(prev => new Set(prev).add('value_condition'));
    }
  };

  const handleDateChange = (date, name) => {
    console.log(`Date change - ${name}:`, { date });
    if (!date) {
      setFormData(prev => ({
        ...prev,
        [name]: null
      }));
      setChangedFields(prev => new Set(prev).add(name));
      return;
    }

    const formattedDate = formatDate(date);
    if (!formattedDate) {
      console.error('Invalid date format:', date);
      toast.error(`Invalid ${name === 'starting_date' ? 'start' : 'end'} date`);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedDate
    }));
    setChangedFields(prev => new Set(prev).add(name));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const token = Cookies.get('accessToken');

      const id = targetData.id
      // Validate id
      if (!id) {
        toast.error('Target ID is missing');
        setIsSubmitting(false);
        return;
      }

      // Validate required fields
      if (!formData.measure) {
        toast.error('Measure is required');
        setIsSubmitting(false);
        return;
      }

      if (!formData.value && formData.value !== 0) {
        toast.error('Target value is required');
        setIsSubmitting(false);
        return;
      }

      // Validate dates
      if (formData.starting_date && formData.end_date) {
        const start = new Date(formData.starting_date);
        const end = new Date(formData.end_date);
        if (start > end) {
          toast.error('End date must be after start date');
          setIsSubmitting(false);
          return;
        }
      }

      // Build payload
      const payload = {
        measure: formData.measure,
        // If API requires measure ID, uncomment:
        // measure: measures.find(m => m.name === formData.measure)?.id,
        value: parseFloat(formData.value) || null,
        value_condition: values,
        starting_date: formData.starting_date ? formatDate(formData.starting_date) : null,
        end_date: formData.end_date ? formatDate(formData.end_date) : null
      };

      // Remove null or undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log('Final payload:', JSON.stringify(payload, null, 2));

      const response = await axios({
        method: 'put',
        url: `${API_URL}/targets/update_target/${id}/`,
        data: payload,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('Update successful:', response.data);
      toast.success('Target updated successfully');
      onSubmit(response.data);
    } catch (error) {
      console.error('Update failed:', {
        status: error.response?.status,
        data: error.response?.data,
        error: error.message
      });

      if (error.response?.data) {
        console.log('Full validation errors:', JSON.stringify(error.response.data, null, 2));
      }

      let errorMessage = 'Failed to update target';

      if (error.response?.status === 422) {
        const validationErrors = error.response.data;
        if (validationErrors) {
          if (typeof validationErrors === 'object') {
            errorMessage = Object.entries(validationErrors)
              .map(([field, messages]) => {
                if (Array.isArray(messages)) {
                  return `${field}: ${messages.join(', ')}`;
                }
                if (typeof messages === 'object') {
                  return `${field}: ${JSON.stringify(messages)}`;
                }
                return `${field}: ${messages}`;
              })
              .join('\n');
          } else if (typeof validationErrors === 'string') {
            errorMessage = validationErrors;
          }
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content add-target-popup" ref={popupRef}>
        <h2>Edit Target</h2>
        {isLoading && <div>Loading measures...</div>}
        <form onSubmit={handleSubmit}>
          <MessageDescription />
          <DataListInput
            id="measures"
            name="measure"
            placeholder="Select Measure"
            value={formData.measure}
            options={measures.map(m => m.name)}
            setValue={(value) => handleChange({ target: { name: 'measure', value } })}
            disabled={isSubmitting || isLoading}
          />
          <DataListInput
            id="type"
            name="type"
            placeholder="Select Type"
            value={type}
            options={typeOptions}
            setValue={handleTypeChange}
            disabled={isSubmitting}
          />
          <TextInput
            type="number"
            id="targetValue"
            name="value"
            placeholder="Target value"
            value={formData.value}
            setValue={(value) => handleChange({ target: { name: 'value', value } })}
            step="0.01"
            min="0.01"
            disabled={isSubmitting}
          />
          <DataListInput
            id="values"
            name="value_condition"
            placeholder="Select Value Condition"
            value={values}
            options={valuesOptions}
            setValue={handleValuesChange}
            disabled={isSubmitting}
          />
          <div className="dateInput">
            <div className="label-dates">
              <label>Start Date</label>
              <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                <DateInput
                  date={formData.starting_date ? dayjs(formData.starting_date) : null}
                  setDate={(date) => handleDateChange(date, 'starting_date')}
                  placeholder="Start Date"
                  label="Start Date"
                  choices={["day", "month", "year"]}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="label-dates">
              <label>End Date</label>
              <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                <DateInput
                  date={formData.end_date ? dayjs(formData.end_date) : null}
                  setDate={(date) => handleDateChange(date, 'end_date')}
                  placeholder="End Date"
                  label="End Date"
                  choices={["day", "month", "year"]}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
          <div className="buttons">
            <SecondaryButton
              onClick={onClose}
              buttonText="Cancel"
              disabled={isSubmitting}
            />
            <PrimaryButton
              isLoading={isSubmitting}
              processingText="Submitting"
              buttonText="Save"
              type="submit"
              disabled={isSubmitting || isLoading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTarget;